import { currentPostedDate } from '@fruitful/fruitils';
import { PaycheckRoutingStatus } from '@fruitful/income-split';
import {
    RECURRING_TRANSFER_FREQUENCY_DESCRIPTION_MAP,
    RecurringTransferEndType,
    type RecurringTransferSetupDetails,
} from '@fruitful/types';
import { zodResolver } from '@hookform/resolvers/zod';
import { add } from 'date-fns';
import { router, useLocalSearchParams } from 'expo-router';
import { useAtom, useSetAtom } from 'jotai';
import * as React from 'react';
import { useEffect, useState } from 'react';
import { type SubmitHandler, useForm } from 'react-hook-form';
import { Keyboard, Pressable } from 'react-native';
import Animated from 'react-native-reanimated';
import {
    BodyMediumRegular,
    Button,
    ButtonSize,
    ButtonVariant,
    CalendarCheckIcon,
    CalendarXIcon,
    ChevronRightIcon,
    ControlledTextInput,
    DateSelectionSheet,
    Edit,
    FootnoteRegular,
    Form,
    FullScreenSpinner,
    Icon,
    IconButton,
    type IconType,
    isWeb,
    LabelRegular,
    redactPrivate,
    RepeatIcon,
    SafeAreaView,
    ScreenContainer,
    SplitIcon,
    StickyNoteIcon,
    StyledH1EmphasizedInput,
    SwitchRow,
    useToastController,
    View,
    XStack,
    YStack,
} from 'smoothie';
import { z } from 'zod';

import ControlledDepositAccountSelect from '../../../common/components/controlled/ControlledDepositAccountSelect';
import { assertNotNullish, isEmpty } from '../../../common/misc';
import { useAuthedUser } from '../../../common/providers/AuthedUserProvider';
import {
    centsToFormattedDollars,
    formatCurrencyInputOnChange,
    formattedDollarsToCents,
    getRecurringTransferEndText,
    MINIMUM_TRANSFER_AMOUNT_CENTS,
} from '../../../common/utils/bankingUtils';
import {
    format,
    formatWithoutTimezone,
    SHORT_READABLE_DATE_FORMAT,
} from '../../../common/utils/dateUtils';
import LogRocketFacade from '../../../logger/log-rocket/LogRocketFacade';
import { useUser } from '../../../user/providers/UserProvider';
import useGetIncomeSplitFromGlobalState from '../../hooks/income-split/useGetIncomeSplitFromGlobalState';
import useGetIncomeSplitFromGlobalStateNew from '../../hooks/income-split/useGetIncomeSplitFromGlobalStateNew';
import { usePlaidEvents } from '../../hooks/plaid/usePlaidEvents';
import useGetAllBankAccountDetails from '../../hooks/useGetAllBankAccountDetails';
import useGetInternalAndExternalDepositAccounts from '../../hooks/useGetInternalAndExternalDepositAccounts';
import {
    preselectedTransferAccountsAtom,
    transferRequestAtom,
} from '../../state/TransferAtoms';
import { selectedDepositoryAccountEligibleForSplit } from '../../utils/IncomeSplitUtils';
import {
    EBBankServicesDisclosure,
    TokenizedAccountNumberDisclosure,
} from '../Disclosures';
import ApplyIncomeSplitSheet from './ApplyIncomeSplitSheet';
import { NeedsReviewSheet } from './NeedsReview';
import RecurringTransferSetupSheet, {
    RecurringTransferSetupStep,
} from './recurring/setup-steps/RecurringTransferSetupSheet';
import SelectAccountSheet from './SelectAccountSheet';

export const depositAccountSchema = z.object({
    id: z.string(),
    isExternal: z.boolean(),
    accountNumberMasked: z.string(),
    accountType: z.string(),
    bankName: z.string(),
    nickname: z.string(),
    availableBalance: z.number().optional(),
    imgBase64: z.string().optional(),
    logo: z.string().optional(),
    icon: z.string().optional(),
    color: z.string().optional(),
    accountNumberMaskedIsTokenized: z.boolean(),
    nonTokenizedAccountNumberMasked: z.string().optional(),
});

export const depositoryTransferSchema = z
    .object({
        amount: z
            .string({
                required_error: 'Please specify a transfer amount',
            })
            .min(1, 'Please specify a transfer amount')
            .max(11, 'Transfer amount is too large'),
        fromDepositAccount: depositAccountSchema.nullable(),
        toDepositAccount: depositAccountSchema.nullable(),
        fromDate: z.date().optional(),
        toDate: z.date().optional(),
        applyIncomeSplit: z.boolean().optional(),
    })
    .refine(({ fromDepositAccount }) => fromDepositAccount != null, {
        message: 'Please select an account',
        path: ['fromDepositAccount'],
    })
    .refine(({ toDepositAccount }) => toDepositAccount != null, {
        message: 'Please select an account',
        path: ['toDepositAccount'],
    })
    .refine(
        ({ fromDepositAccount, toDepositAccount }) =>
            fromDepositAccount &&
            toDepositAccount &&
            (!fromDepositAccount.isExternal || !toDepositAccount.isExternal),
        {
            message: 'Transfers between two external accounts not allowed',
            path: ['toDepositAccount'],
        },
    );

export type DepositoryTransferSchema = z.infer<typeof depositoryTransferSchema>;

const FORM_MAX_WIDTH = 500;

const TransferForm = () => {
    LogRocketFacade.tagPage('TransferForm');

    const { context } = useLocalSearchParams();
    const isFirstFunding = context === 'firstFunding';

    const user = useAuthedUser();
    const { refetch: refetchUser } = useUser();

    const setTransferRequest = useSetAtom(transferRequestAtom);
    const [preselectedTransferAccounts, setPreselectedTransferAccounts] =
        useAtom(preselectedTransferAccountsAtom);

    const [isSelectFromAccountSheetOpen, setIsSelectFromAccountSheetOpen] =
        useState(false);
    const [isSelectToAccountSheetOpen, setIsSelectToAccountSheetOpen] =
        useState(false);

    const [isRecurring, setIsRecurring] = useState(false);
    const [applyIncomeSplit, setApplyIncomeSplit] = useState(false);
    const [isRecurringSetupSheetOpen, setIsRecurringSetupSheetOpen] =
        useState(false);
    const [isApplyIncomeSplitSheetOpen, setIsIncomeSplitSheetOpen] =
        useState(false);
    const [goToRecurringSetupStep, setGoToRecurringSetupStep] = useState(
        RecurringTransferSetupStep.DESCRIPTION,
    );
    const [recurringTransferDetails, setRecurringTransferDetails] =
        useState<RecurringTransferSetupDetails>({});

    const [isValid, setIsValid] = useState(false);

    const [showEmptyScreen, setShowEmptyScreen] = useState(false);

    const [isStartDateSheetOpen, setIsStartDateSheetOpen] = useState(false);
    const [shouldReOpenFlowSheet, setShouldReOpenFlowSheet] = useState(false);
    const [isEndDateSheetOpen, setIsEndDateSheetOpen] = useState(false);
    const [isReviewSheetOpen, setIsReviewSheetOpen] = useState(false);

    const maxStartDate = add(new Date(), { days: 60 });

    const maxEndDate = new Date();
    maxEndDate.setFullYear(maxEndDate.getFullYear() + 10);

    usePlaidEvents(setShowEmptyScreen);
    const toast = useToastController();

    const { incomeSplit: incomeSplitLegacy } =
        useGetIncomeSplitFromGlobalState();
    const { incomeSplit } = useGetIncomeSplitFromGlobalStateNew();

    const hasExistingIncomeSplitLegacy =
        (incomeSplitLegacy?.rules?.length ?? 0) > 0;
    const hasExistingIncomeSplit = (incomeSplit?.ruleSets?.length ?? 0) > 0;

    const incomeSplitIsActive = hasExistingIncomeSplit
        ? incomeSplit?.status === PaycheckRoutingStatus.ACTIVE
        : hasExistingIncomeSplitLegacy
          ? incomeSplitLegacy?.status === PaycheckRoutingStatus.ACTIVE
          : false;

    const {
        data: depositAccounts,
        isLoading: isLoadingDepositAccounts,
        refetch: refetchDepositAccounts,
    } = useGetInternalAndExternalDepositAccounts({});

    const { isLoading: isLoadingBankAccountDetails, bankAccountDetails } =
        useGetAllBankAccountDetails();

    const onSuccessfulPlaidLink = () => {
        void refetchUser?.();
        void refetchDepositAccounts();
    };

    const {
        watch,
        setValue,
        setError,
        clearErrors,
        register,
        control,
        setFocus,
        handleSubmit,
        formState: { errors },
        reset,
    } = useForm<DepositoryTransferSchema>({
        resolver: zodResolver(depositoryTransferSchema),
        defaultValues: {
            amount: '',
            fromDepositAccount: null,
            toDepositAccount: null,
            applyIncomeSplit: false,
        },
    });

    const handleEndDateSelect = (endDate: Date): void => {
        if (
            recurringTransferDetails.start &&
            endDate < recurringTransferDetails.start
        ) {
            toast.show(
                'Please select an end date that is later than the start date.',
            );
            return;
        }
        setRecurringTransferDetails((prev) => ({
            ...prev,
            end: {
                type: RecurringTransferEndType.ON,
                date: endDate,
            },
        }));
        setIsEndDateSheetOpen(false);
    };

    const handleStartDateSelect = (fromDate: Date): void => {
        if (
            recurringTransferDetails.end?.date &&
            fromDate > recurringTransferDetails.end.date
        ) {
            toast.show(
                'Please select a start date that is earlier than the end date.',
            );
            return;
        }
        setRecurringTransferDetails((prev) => ({
            ...prev,
            start: fromDate,
        }));
        setIsStartDateSheetOpen(false);

        if (shouldReOpenFlowSheet) {
            setGoToRecurringSetupStep(RecurringTransferSetupStep.END_TYPE);
            setIsRecurringSetupSheetOpen(true);
            setShouldReOpenFlowSheet(false);
        }
    };

    useEffect(() => {
        if (isFirstFunding) {
            // For first account opening, assume there's on external linked account and one internal account to pre-select
            const externalAccount = (depositAccounts ?? []).find(
                (d) => d.isExternal && d.verified,
            );
            const internalAccount = (depositAccounts ?? []).find(
                (d) => !d.isExternal && d.verified,
            );
            externalAccount && setValue('fromDepositAccount', externalAccount);
            internalAccount && setValue('toDepositAccount', internalAccount);
        } else {
            const { toSyncteraAccountId, fromSyncteraAccountId } =
                preselectedTransferAccounts ?? {};

            if (toSyncteraAccountId != null) {
                const account = (depositAccounts ?? []).find(
                    (d) => d.id === toSyncteraAccountId,
                );
                account && setValue('toDepositAccount', account);
            }

            if (fromSyncteraAccountId != null) {
                const account = (depositAccounts ?? []).find(
                    (d) => d.id === fromSyncteraAccountId,
                );
                account && setValue('fromDepositAccount', account);
            }
        }
    }, [depositAccounts, isFirstFunding]);

    const submitTransfer: SubmitHandler<DepositoryTransferSchema> = ({
        toDepositAccount,
        fromDepositAccount,
        amount,
    }) => {
        if (!isValid) return;

        if (toDepositAccount == null || fromDepositAccount == null) {
            // Form validation will handle showing appropriate errors if these are null
            // Zod typing isn't smart enough to account for the checks in refine
            return;
        }

        // Clear out pre-selections from global state
        setPreselectedTransferAccounts(null);

        setTransferRequest({
            toDepositAccount,
            fromDepositAccount,
            amount: formattedDollarsToCents(amount),
            applyIncomeSplit,
            ...(isRecurring ? { recurring: recurringTransferDetails } : {}),
        });
        router.navigate({
            pathname: '/banking/transfer-confirm',
            params: { context },
        });
    };

    const selectedFromAccount = watch('fromDepositAccount');
    const selectedToAccount = watch('toDepositAccount');
    const amount = watch('amount');

    const isTransferStateValid = (): boolean => {
        const amountCents = formattedDollarsToCents(amount);
        if (
            selectedToAccount == null ||
            selectedFromAccount == null ||
            isNaN(amountCents)
        ) {
            return false;
        }

        if (amountCents < MINIMUM_TRANSFER_AMOUNT_CENTS) {
            setError('amount', {
                message: `${centsToFormattedDollars(MINIMUM_TRANSFER_AMOUNT_CENTS)} is the minimum`,
            });
            return false;
        }

        if (amountCents >= 10000000000) {
            setError('amount', { message: 'Maximum $99,999,999.99' });
            return false;
        }

        // We can't predict if funds will be enough for recurring transfers.
        // We only have balance information for internal accounts.
        if (
            !isRecurring &&
            !selectedFromAccount.isExternal &&
            amountCents > assertNotNullish(selectedFromAccount.availableBalance)
        ) {
            setError('amount', { message: 'Insufficient funds available' });
            return false;
        }

        if (isRecurring) {
            const { frequency, start, end } = recurringTransferDetails;
            const { type: endType, date: endDate, count: endCount } = end ?? {};

            if (frequency == null || start == null || isEmpty(end)) {
                return false;
            }

            if (endType === RecurringTransferEndType.ON && endDate == null) {
                return false;
            }

            if (
                endType === RecurringTransferEndType.AFTER &&
                endCount == null
            ) {
                return false;
            }
            if (start != null && end?.date != null && start > end.date) {
                setError('toDate', {
                    message: 'End date must be after start date',
                });
                return false;
            }
        }

        clearErrors();
        return true;
    };

    useEffect(() => {
        setIsValid(isTransferStateValid());
    }, [
        selectedFromAccount,
        selectedToAccount,
        amount,
        isRecurring,
        applyIncomeSplit,
        JSON.stringify(recurringTransferDetails),
    ]);

    if (isLoadingDepositAccounts || isLoadingBankAccountDetails) {
        return <FullScreenSpinner />;
    }

    if (showEmptyScreen) {
        return (
            <SafeAreaView
                style={{ flex: 1, height: '100%', backgroundColor: 'white' }}
            ></SafeAreaView>
        );
    }

    const showIncomeSplitToggle =
        incomeSplitIsActive && !isRecurring && !selectedToAccount?.isExternal;

    // Eligible if solo membership OR the account is joint
    const selectedToAccountEligibleForSplit =
        selectedDepositoryAccountEligibleForSplit(
            user,
            bankAccountDetails,
            selectedToAccount,
        );

    return (
        <Pressable
            onPress={Keyboard.dismiss}
            style={{ flex: 1 }}
            disabled={isWeb}
        >
            <ScreenContainer
                title="Transfer"
                hasBackButton={!isFirstFunding}
                onBeforeGoingBack={() => {
                    setPreselectedTransferAccounts(null);
                    reset();
                }}
                onFallbackRoute="/home/banking"
            >
                <YStack gap={24} justifyContent="space-between">
                    <YStack>
                        <Form onSubmit={handleSubmit(submitTransfer)}>
                            <YStack
                                alignItems="center"
                                marginTop="10%"
                                marginBottom={24}
                            >
                                <Animated.View>
                                    <XStack justifyContent="space-between">
                                        <ControlledTextInput
                                            Input={StyledH1EmphasizedInput}
                                            type="numeric"
                                            name="amount"
                                            control={control}
                                            register={register}
                                            errors={errors}
                                            height="auto"
                                            enterKeyHint="done"
                                            textAlign="center"
                                            borderWidth={0}
                                            maxWidth={FORM_MAX_WIDTH}
                                            placeholder="$0"
                                            placeholderTextColor="$black"
                                            formatValueOnChange={
                                                formatCurrencyInputOnChange
                                            }
                                            containerStyle={{
                                                alignItems: 'center',
                                                paddingHorizontal: 8,
                                            }}
                                            {...redactPrivate()}
                                        />
                                        <IconButton
                                            variant="secondary"
                                            icon={Edit}
                                            onPress={() => {
                                                setFocus('amount');
                                            }}
                                        />
                                    </XStack>
                                </Animated.View>
                            </YStack>
                            <YStack gap={16}>
                                <YStack>
                                    <ControlledDepositAccountSelect
                                        name="fromDepositAccount"
                                        control={control}
                                        register={register}
                                        errors={errors}
                                        label="From"
                                        placeholder="Select your account"
                                        onPress={() => {
                                            setIsSelectFromAccountSheetOpen(
                                                true,
                                            );
                                            Keyboard.dismiss();
                                        }}
                                    />
                                    {selectedFromAccount?.accountNumberMaskedIsTokenized &&
                                        selectedFromAccount?.nonTokenizedAccountNumberMasked !=
                                            null && (
                                            <TokenizedAccountNumberDisclosure
                                                account={selectedFromAccount}
                                                marginTop={8}
                                            />
                                        )}
                                </YStack>
                                <YStack>
                                    <ControlledDepositAccountSelect
                                        name="toDepositAccount"
                                        control={control}
                                        register={register}
                                        errors={errors}
                                        label="To"
                                        placeholder="Select your account"
                                        onPress={() => {
                                            setIsSelectToAccountSheetOpen(true);
                                            Keyboard.dismiss();
                                        }}
                                    />
                                    {selectedToAccount?.accountNumberMaskedIsTokenized &&
                                        selectedToAccount?.nonTokenizedAccountNumberMasked !=
                                            null && (
                                            <TokenizedAccountNumberDisclosure
                                                account={selectedToAccount}
                                                marginTop={8}
                                            />
                                        )}
                                </YStack>
                            </YStack>
                        </Form>
                        <YStack marginTop={30}>
                            <XStack
                                alignItems="center"
                                justifyContent="space-between"
                                marginBottom={showIncomeSplitToggle ? 0 : 12}
                            >
                                <SwitchRow
                                    containerProps={{
                                        borderTopLeftRadius: 24,
                                        borderTopRightRadius: 24,
                                        borderBottomLeftRadius:
                                            showIncomeSplitToggle || isRecurring
                                                ? 0
                                                : 24,
                                        borderBottomRightRadius:
                                            showIncomeSplitToggle || isRecurring
                                                ? 0
                                                : 24,
                                    }}
                                    switchProps={{
                                        style: { marginRight: -5 },
                                        isChecked: isRecurring,
                                        onCheckedChange: (checked) => {
                                            setIsRecurring(checked);
                                            if (!checked)
                                                setRecurringTransferDetails({});
                                        },
                                    }}
                                    title="Repeat this transfer?"
                                />
                            </XStack>
                            {isRecurring && (
                                <YStack
                                    marginBottom={
                                        showIncomeSplitToggle ? 0 : 16
                                    }
                                >
                                    <RecurringOption
                                        icon={StickyNoteIcon}
                                        label="Description"
                                        value={
                                            recurringTransferDetails.description
                                        }
                                        onPress={() => {
                                            setGoToRecurringSetupStep(
                                                RecurringTransferSetupStep.DESCRIPTION,
                                            );
                                            setIsRecurringSetupSheetOpen(true);
                                        }}
                                    />
                                    <RecurringOption
                                        icon={RepeatIcon}
                                        label="Repeats"
                                        value={
                                            recurringTransferDetails.frequency !=
                                            null
                                                ? RECURRING_TRANSFER_FREQUENCY_DESCRIPTION_MAP[
                                                      recurringTransferDetails
                                                          .frequency
                                                  ]
                                                : undefined
                                        }
                                        onPress={() => {
                                            setGoToRecurringSetupStep(
                                                RecurringTransferSetupStep.FREQUENCY,
                                            );
                                            setIsRecurringSetupSheetOpen(true);
                                        }}
                                    />
                                    <RecurringOption
                                        icon={CalendarCheckIcon}
                                        label="Starts"
                                        value={
                                            recurringTransferDetails.start !=
                                            null
                                                ? format(
                                                      recurringTransferDetails.start,
                                                      SHORT_READABLE_DATE_FORMAT,
                                                  )
                                                : undefined
                                        }
                                        onPress={() => {
                                            setIsStartDateSheetOpen(true);
                                        }}
                                    />
                                    <RecurringOption
                                        icon={CalendarXIcon}
                                        label="Ends"
                                        value={getRecurringTransferEndText(
                                            recurringTransferDetails.end?.type,
                                            recurringTransferDetails.end
                                                ?.date != null
                                                ? formatWithoutTimezone(
                                                      recurringTransferDetails
                                                          .end?.date,
                                                      SHORT_READABLE_DATE_FORMAT,
                                                  )
                                                : undefined,
                                            recurringTransferDetails.end?.count,
                                        )}
                                        onPress={() => {
                                            setGoToRecurringSetupStep(
                                                RecurringTransferSetupStep.END_TYPE,
                                            );
                                            setIsRecurringSetupSheetOpen(true);
                                        }}
                                    />
                                    {selectedToAccountEligibleForSplit &&
                                        !selectedToAccount?.isExternal && (
                                            <RecurringOption
                                                icon={SplitIcon}
                                                label="Apply Income Split rules"
                                                value={
                                                    applyIncomeSplit
                                                        ? 'Yes'
                                                        : 'No'
                                                }
                                                onPress={() => {
                                                    setIsIncomeSplitSheetOpen(
                                                        true,
                                                    );
                                                }}
                                            />
                                        )}
                                </YStack>
                            )}
                            {showIncomeSplitToggle && (
                                <>
                                    <XStack
                                        alignItems="center"
                                        justifyContent="space-between"
                                        marginBottom={12}
                                    >
                                        <SwitchRow
                                            containerProps={{
                                                borderRadius: 24,
                                                borderTopLeftRadius: 0,
                                                borderTopRightRadius: 0,
                                            }}
                                            switchProps={{
                                                isChecked: applyIncomeSplit,
                                                onCheckedChange: (checked) => {
                                                    setApplyIncomeSplit(
                                                        checked,
                                                    );
                                                },
                                                style: {
                                                    marginRight: -5,
                                                },
                                                disabled:
                                                    selectedToAccount != null &&
                                                    !selectedToAccountEligibleForSplit,
                                            }}
                                            title="Apply Income Split rules?"
                                            titleProps={{
                                                color:
                                                    selectedToAccount != null &&
                                                    !selectedToAccountEligibleForSplit
                                                        ? '$gray700'
                                                        : '$black',
                                            }}
                                        />
                                    </XStack>

                                    {selectedToAccount != null &&
                                        !selectedToAccountEligibleForSplit && (
                                            <FootnoteRegular color="$gray700">
                                                Income Split can only be applied
                                                to deposits arriving in joint
                                                Cash accounts.
                                            </FootnoteRegular>
                                        )}
                                </>
                            )}
                        </YStack>
                    </YStack>
                    <YStack paddingTop={16} paddingBottom={16}>
                        <Button
                            variant={ButtonVariant.PRIMARY}
                            size={ButtonSize.LARGE}
                            text="Review transfer"
                            onPress={handleSubmit(submitTransfer)}
                            disabled={!isValid}
                        />
                        <EBBankServicesDisclosure marginTop={16} />
                        {isFirstFunding && (
                            <View marginTop={8}>
                                <Button
                                    variant={ButtonVariant.TERTIARY}
                                    size={ButtonSize.SMALL}
                                    text="Skip for now"
                                    onPress={() => {
                                        router.navigate({
                                            pathname:
                                                '/banking/create-edit-space',
                                            params: {
                                                fromKyc: 'true',
                                            },
                                        });
                                    }}
                                />
                            </View>
                        )}
                    </YStack>

                    {depositAccounts && (
                        <>
                            <SelectAccountSheet
                                isOpen={isSelectFromAccountSheetOpen}
                                depositAccounts={depositAccounts}
                                onClose={() => {
                                    setIsSelectFromAccountSheetOpen(false);
                                }}
                                onPlaidExit={() => {
                                    setShowEmptyScreen(false);
                                }}
                                onSelectAccount={(account) => {
                                    if (account.id === selectedToAccount?.id) {
                                        setValue('toDepositAccount', null);
                                    }
                                    setValue('fromDepositAccount', account);
                                    clearErrors();
                                }}
                                enableLinkingNewAccount={
                                    user.externalAccountLinkingEnabled ?? false
                                }
                                onSuccessfulPlaidLink={onSuccessfulPlaidLink}
                                onNeedsReview={() => {
                                    setIsReviewSheetOpen(true);
                                }}
                            />
                            <SelectAccountSheet
                                isOpen={isSelectToAccountSheetOpen}
                                depositAccounts={depositAccounts}
                                onClose={() => {
                                    setIsSelectToAccountSheetOpen(false);
                                }}
                                onPlaidExit={() => {
                                    setShowEmptyScreen(false);
                                }}
                                onSelectAccount={(account) => {
                                    if (
                                        account.id === selectedFromAccount?.id
                                    ) {
                                        setValue('fromDepositAccount', null);
                                    }
                                    if (
                                        account.isExternal ||
                                        selectedDepositoryAccountEligibleForSplit(
                                            user,
                                            bankAccountDetails,
                                            selectedToAccount,
                                        )
                                    ) {
                                        setApplyIncomeSplit(false);
                                    }
                                    setValue('toDepositAccount', account);
                                    clearErrors();
                                }}
                                enableLinkingNewAccount={
                                    user.externalAccountLinkingEnabled ?? false
                                }
                                onSuccessfulPlaidLink={onSuccessfulPlaidLink}
                                onNeedsReview={() => {
                                    setIsReviewSheetOpen(true);
                                }}
                            />
                        </>
                    )}
                    <RecurringTransferSetupSheet
                        isOpen={isRecurringSetupSheetOpen}
                        details={recurringTransferDetails}
                        step={goToRecurringSetupStep}
                        onClose={() => {
                            setIsRecurringSetupSheetOpen(false);
                        }}
                        onGoToStep={(step) => {
                            setGoToRecurringSetupStep(step);
                        }}
                        onUpdateDetails={(
                            details: RecurringTransferSetupDetails,
                        ) => {
                            setRecurringTransferDetails({
                                ...recurringTransferDetails,
                                ...details,
                            });
                        }}
                        onOpenStartDateSheet={() => {
                            setIsStartDateSheetOpen(true);
                            setIsRecurringSetupSheetOpen(false);
                            setShouldReOpenFlowSheet(true);
                        }}
                        onOpenEndDateSheet={() => {
                            setIsEndDateSheetOpen(true);
                        }}
                    />
                    <ApplyIncomeSplitSheet
                        isOpen={isApplyIncomeSplitSheetOpen}
                        onClose={() => {
                            setIsIncomeSplitSheetOpen(false);
                        }}
                        onPress={(checked) => {
                            setApplyIncomeSplit(checked);
                            setIsIncomeSplitSheetOpen(false);
                        }}
                        applyIncomeSplit={applyIncomeSplit}
                    />
                </YStack>
                <DateSelectionSheet
                    isOpen={isStartDateSheetOpen}
                    onClose={() => {
                        setIsStartDateSheetOpen(false);
                    }}
                    defaultValue={currentPostedDate()}
                    minimumDate={currentPostedDate()}
                    maximumDate={maxStartDate}
                    title="Select a start date"
                    confirmButtonText="Set start date"
                    onConfirm={handleStartDateSelect}
                />
                <DateSelectionSheet
                    isOpen={isEndDateSheetOpen}
                    onClose={() => {
                        setIsEndDateSheetOpen(false);
                    }}
                    minimumDate={
                        recurringTransferDetails.start ?? currentPostedDate()
                    }
                    maximumDate={maxEndDate} // If a max date is not provided, on web the date selector will default to current year
                    title="Select an end date"
                    confirmButtonText="Set end date"
                    onConfirm={handleEndDateSelect}
                    defaultValue={
                        recurringTransferDetails.start ?? currentPostedDate()
                    }
                />
                <NeedsReviewSheet
                    isOpen={isReviewSheetOpen}
                    onClose={() => {
                        setIsReviewSheetOpen(false);
                    }}
                />
            </ScreenContainer>
        </Pressable>
    );
};

interface RecurringOptionProps {
    icon: IconType;
    label: string;
    value?: string;
    onPress: () => void;
}

const RecurringOption = ({
    icon,
    label,
    value,
    onPress,
}: RecurringOptionProps) => {
    return (
        <XStack
            paddingVertical={6}
            alignItems="center"
            justifyContent="space-between"
            onPress={onPress}
            cursor="pointer"
            hoverStyle={{ backgroundColor: '$gray100' }}
            $sm={{ paddingHorizontal: 8, borderRadius: 24 }}
        >
            <XStack alignItems="center" gap={8}>
                <View backgroundColor="$gray200" borderRadius={24} padding={12}>
                    <Icon icon={icon} size={16} color="$gray1100" />
                </View>
                {!isEmpty(value) ? (
                    <YStack gap={2}>
                        <LabelRegular color="$gray1000">{label}</LabelRegular>
                        <BodyMediumRegular maxWidth={280} numberOfLines={1}>
                            {value}
                        </BodyMediumRegular>
                    </YStack>
                ) : (
                    <BodyMediumRegular>{label}</BodyMediumRegular>
                )}
            </XStack>
            <Icon icon={ChevronRightIcon} size={16} color="$gray1200" />
        </XStack>
    );
};

export default TransferForm;
